import React, { useState, useEffect, useRef } from 'react';
import { chatAPI, Conversation, Message, filesAPI } from '../services/api';
import { MessageSquare, Send, Plus, Trash2, LogOut, Code, Eye, Zap, Paperclip, Copy, Menu, X, ChevronDown } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

interface ChatProps {
  onLogout: () => void;
}

const Chat: React.FC<ChatProps> = ({ onLogout }) => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversation, setCurrentConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<'auto' | 'code' | 'vision'>('auto');
  const [selectedModel, setSelectedModel] = useState<string>('default');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [isMobile, setIsMobile] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const username = localStorage.getItem('username') || 'User';

  useEffect(() => {
    loadConversations();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) {
        setIsSidebarOpen(false);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const markdownComponents = {
    code({ node, inline, className, children, ...props }: any) {
      const codeText = String(children).replace(/\n$/, '');
      if (inline) {
        return (
          <code className={className} {...props}>
            {children}
          </code>
        );
      }
      const handleCopy = () => {
        navigator.clipboard.writeText(codeText).catch(() => {});
      };
      return (
        <div className="code-block">
          <button type="button" className="copy-button" onClick={handleCopy}>
            <Copy size={12} />
            <span>Copy</span>
          </button>
          <pre>
            <code className={className} {...props}>
              {codeText}
            </code>
          </pre>
        </div>
      );
    },
  };

  const loadConversations = async () => {
    try {
      const data = await chatAPI.getConversations();
      setConversations(data);
    } catch (error) {
      console.error('Failed to load conversations:', error);
    }
  };

  const loadConversation = async (id: number) => {
    try {
      const data = await chatAPI.getConversation(id);
      setCurrentConversation(data);
      setMessages(data.messages || []);
    } catch (error) {
      console.error('Failed to load conversation:', error);
    }
  };

  const createNewChat = () => {
    setCurrentConversation(null);
    setMessages([]);
  };

  const handleFilesSelected = (files: FileList) => {
    const newFiles = Array.from(files);
    if (!newFiles.length) return;
    setAttachedFiles((prev) => [...prev, ...newFiles]);
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const files = e.clipboardData.files;
    if (files && files.length) {
      handleFilesSelected(files);
    }
  };

  const handleRemoveFile = (name: string) => {
    setAttachedFiles((prev) => prev.filter((f) => f.name !== name));
  };

  const deleteConversation = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm('Delete this conversation?')) return;

    try {
      await chatAPI.deleteConversation(id);
      if (currentConversation?.id === id) {
        createNewChat();
      }
      loadConversations();
    } catch (error) {
      console.error('Failed to delete conversation:', error);
    }
  };

  const readFileAsBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Strip data URL prefix to get raw base64
        const base64 = result.includes(',') ? result.split(',')[1] : result;
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const readFileAsText = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsText(file);
    });
  };

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input;
    setInput('');
    setLoading(true);

    // Optimistic UI update
    const tempMessage: Message = {
      id: Date.now(),
      role: 'user',
      content: userMessage,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, tempMessage]);

    try {
      let filePayloads: any[] = [];
      if (attachedFiles.length) {
        try {
          filePayloads = await Promise.all(
            attachedFiles.map(async (file) => {
              const isImage = file.type.startsWith('image/');
              const base64 = await readFileAsBase64(file);
              let textContent: string | undefined;
              if (!isImage) {
                try {
                  textContent = await readFileAsText(file);
                } catch {}
              }
              return {
                filename: file.name,
                content_type: file.type,
                size: file.size,
                base64,
                ...(textContent !== undefined ? { text_content: textContent } : {}),
              };
            })
          );
        } catch (err) {
          console.error('Failed to read files:', err);
        }
      }

      const response = await chatAPI.sendMessage(
        userMessage,
        currentConversation?.id,
        mode,
        filePayloads.length ? filePayloads : undefined,
        selectedModel !== 'default' ? selectedModel : undefined
      );

      // Update messages
      setMessages((prev) => [
        ...prev,
        {
          id: response.message_id,
          role: 'assistant',
          content: response.response,
          model_used: response.model_used,
          created_at: new Date().toISOString(),
          web_search_used: response.web_search_used,
        },
      ]);

      // Update conversation list
      if (!currentConversation) {
        await loadConversations();
        const newConv = await chatAPI.getConversation(response.conversation_id);
        setCurrentConversation(newConv);
      } else {
        loadConversations();
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      setMessages((prev) => prev.slice(0, -1)); // Remove optimistic message
    } finally {
      setLoading(false);
      setAttachedFiles([]);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('username');
    onLogout();
  };

  const onConvClick = (id: number) => {
    loadConversation(id);
    if (isMobile) setIsSidebarOpen(false);
  };

  const onNewChat = () => {
    createNewChat();
    if (isMobile) setIsSidebarOpen(false);
  };

  const sidebarContent = (
    <>
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between gap-2">
          <button
            onClick={onNewChat}
            className="flex-1 bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-3 rounded-lg flex items-center justify-center gap-2 transition"
          >
            <Plus size={18} />
            New Chat
          </button>
          {isMobile && (
            <button
              type="button"
              className="icon-button p-2"
              onClick={() => setIsSidebarOpen(false)}
            >
              <X size={20} />
            </button>
          )}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {conversations.map((conv) => (
          <div
            key={conv.id}
            onClick={() => onConvClick(conv.id)}
            className={`p-3 rounded-lg cursor-pointer transition group ${
              currentConversation?.id === conv.id
                ? 'bg-purple-50 border border-purple-200'
                : 'hover:bg-gray-50'
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <MessageSquare size={16} className="text-purple-600 flex-shrink-0" />
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {conv.title}
                  </p>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {conv.message_count} messages
                </p>
              </div>
              <button
                onClick={(e) => deleteConversation(conv.id, e)}
                className="icon-button opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700"
              >
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>
      <div className="p-4 border-t border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img
              src={`https://api.dicebear.com/7.x/bottts/svg?seed=${encodeURIComponent(username)}`}
              alt="Profile"
              className="w-8 h-8 rounded-full"
            />
            <span className="text-sm font-medium text-gray-700">{username}</span>
          </div>
          <button
            onClick={handleLogout}
            className="icon-button text-gray-500 hover:text-red-600 p-2"
            title="Logout"
          >
            <LogOut size={20} />
          </button>
        </div>
      </div>
    </>
  );

  const messageList = (
    <>
      {messages.map((message) => (
        <div
          key={message.id}
          className={`flex gap-3 ${
            message.role === 'user' ? 'justify-end' : 'justify-start'
          }`}
        >
          {message.role === 'assistant' && (
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center text-white font-semibold flex-shrink-0" style={{ fontSize: '0.7rem' }}>
              AI
            </div>
          )}
          <div
            className={`max-w-3xl px-4 py-3 rounded-2xl ${
              message.role === 'user'
                ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white'
                : 'bg-white border border-gray-200'
            }`}
          >
            {message.role === 'assistant' ? (
              <div className="prose prose-sm max-w-none">
                <ReactMarkdown components={markdownComponents}>
                  {message.content}
                </ReactMarkdown>
              </div>
            ) : (
              <p className="text-sm">{message.content}</p>
            )}
            <div className="flex items-center gap-2 mt-2">
              {message.model_used && (
                <span className="text-xs text-purple-500 italic">
                  {message.model_used}
                </span>
              )}
              {message.web_search_used && (
                <span className="text-xs text-green-600 italic">
                  Web search
                </span>
              )}
            </div>
          </div>
        </div>
      ))}
      {loading && (
        <div className="flex items-center gap-2 text-sm text-gray-500 mt-4">
          <span className="spinner" />
          <span>Thinking...</span>
        </div>
      )}
      <div ref={messagesEndRef} />
    </>
  );

  return (
    <div className="flex h-screen bg-gray-100 chat-container">
      {isMobile && isSidebarOpen && (
        <div
          className="chat-sidebar-backdrop"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`w-80 bg-white border-r border-gray-200 flex flex-col chat-sidebar${
          isMobile ? (isSidebarOpen ? ' open' : ' closed') : ''
        }`}
      >
        {sidebarContent}
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col chat-main">

        {/* ===== DESKTOP header ===== */}
        <div className="bg-white border-b border-gray-200 p-4 desktop-header">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img
                src="https://www-new.zippyops.com/wp-content/uploads/sites/9/2026/01/cropped-rectcolour-scaled-1.png"
                alt="ZippyOPS"
                className="h-8"
              />
              <h1 className="text-xl font-bold text-gray-800">
                {currentConversation?.title || 'New Chat'}
              </h1>
            </div>
            <div className="flex gap-2 items-center">
              <button onClick={() => setMode('auto')} className={`px-4 py-2 rounded-lg flex items-center gap-2 ${mode === 'auto' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                <Zap size={16} /> Auto
              </button>
              <button onClick={() => setMode('code')} className={`px-4 py-2 rounded-lg flex items-center gap-2 ${mode === 'code' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                <Code size={16} /> Code
              </button>
              <button onClick={() => setMode('vision')} className={`px-4 py-2 rounded-lg flex items-center gap-2 ${mode === 'vision' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                <Eye size={16} /> Vision
              </button>
              <div className="ml-2 border-l border-gray-300 pl-2">
                <select
                  value={selectedModel}
                  onChange={(e) => setSelectedModel(e.target.value)}
                  className="px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent cursor-pointer"
                  title="Select model"
                >
                  <option value="default">Default Model</option>
                  <option value="mistral-nemo:12b">Mistral Nemo 12B</option>
                  <option value="qwen3.5:9b">Qwen 3.5 9B</option>
                  <option value="qwen2.5-coder:14b">Qwen Coder 14B</option>
                  <option value="deepseek-coder-v2:16b">DeepSeek Coder V2 16B</option>
                  <option value="qwen2.5vl:7b">Qwen VL 7B</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* ===== DESKTOP messages ===== */}
        <div className="flex-1 overflow-y-auto p-6 desktop-messages">
          <div className="max-w-4xl mx-auto space-y-6">
            {messageList}
          </div>
        </div>

        {/* ===== DESKTOP input ===== */}
        <div className="bg-white border-t border-gray-200 p-4 desktop-input">
          <div className="max-w-4xl mx-auto flex flex-col gap-3">
            {attachedFiles.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {attachedFiles.map((file) => (
                  <div key={file.name + file.lastModified} className="flex items-center gap-2 px-2 py-1 border border-gray-300 rounded-lg text-xs bg-gray-50">
                    <span>{file.name}</span>
                    <button type="button" onClick={() => handleRemoveFile(file.name)} className="text-gray-500">×</button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-3">
              <button type="button" onClick={() => fileInputRef.current?.click()} className="px-3 py-3 border border-gray-300 rounded-lg bg-gray-50 flex items-center justify-center" disabled={loading}>
                <Paperclip size={18} />
              </button>
              <input type="file" multiple ref={fileInputRef} style={{ display: 'none' }} onChange={(e) => e.target.files && handleFilesSelected(e.target.files)} />
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                onPaste={handlePaste}
                placeholder="Type your message... (Shift+Enter for new line)"
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                disabled={loading}
                rows={1}
              />
              <button onClick={sendMessage} disabled={loading || !input.trim()} className="px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50 transition flex items-center gap-2">
                <Send size={20} />
                {loading ? 'Sending...' : 'Send'}
              </button>
            </div>
          </div>
        </div>

        {/* ===== MOBILE header ===== */}
        <div className="mobile-header">
          <div className="mobile-header-left">
            <button type="button" className="icon-button" onClick={() => setIsSidebarOpen(true)}>
              <Menu size={22} />
            </button>
            <img src="https://www-new.zippyops.com/wp-content/uploads/sites/9/2026/01/cropped-rectcolour-scaled-1.png" alt="ZippyOPS" style={{ height: '24px' }} />
            <h1>{currentConversation?.title || 'New Chat'}</h1>
          </div>
        </div>

        {/* ===== MOBILE mode bar ===== */}
        <div className="mobile-mode-bar">
          <button onClick={() => setMode('auto')} className={`mobile-mode-btn${mode === 'auto' ? ' active' : ''}`}>
            <Zap size={12} /> Auto
          </button>
          <button onClick={() => setMode('code')} className={`mobile-mode-btn${mode === 'code' ? ' active' : ''}`}>
            <Code size={12} /> Code
          </button>
          <button onClick={() => setMode('vision')} className={`mobile-mode-btn${mode === 'vision' ? ' active' : ''}`}>
            <Eye size={12} /> Vision
          </button>
          <select
            value={selectedModel}
            onChange={(e) => setSelectedModel(e.target.value)}
            className="mobile-model-select"
            title="Select model"
          >
            <option value="default">Default</option>
            <option value="mistral-nemo:12b">Mistral Nemo</option>
            <option value="qwen3.5:9b">Qwen 3.5</option>
            <option value="qwen2.5-coder:14b">Qwen Coder</option>
            <option value="deepseek-coder-v2:16b">DeepSeek V2</option>
            <option value="qwen2.5vl:7b">Qwen VL</option>
          </select>
        </div>

        {/* ===== MOBILE messages ===== */}
        <div className="mobile-messages">
          <div className="space-y-6">
            {messageList}
          </div>
        </div>

        {/* ===== MOBILE input ===== */}
        <div className="mobile-input-bar">
          <button type="button" className="mobile-attach-btn" onClick={() => fileInputRef.current?.click()} disabled={loading}>
            <Paperclip size={18} />
          </button>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
            onPaste={handlePaste}
            placeholder="Type a message..."
            disabled={loading}
            rows={1}
          />
          <button onClick={sendMessage} disabled={loading || !input.trim()} className="mobile-send-btn">
            <Send size={16} />
          </button>
        </div>

      </div>
    </div>
  );
};

export default Chat;
